import { motion } from "framer-motion";
import { Code, Server, PenTool, Wrench, Users } from "lucide-react";
import { Skill } from "@/lib/index";

interface SkillCardProps {
  skill: Skill;
}

/**
 * Maps skill categories to relevant Lucide icons
 */
const getCategoryIcon = (category: Skill['category']) => {
  switch (category) {
    case 'Frontend':
      return <Code className="w-4 h-4" />;
    case 'Backend':
      return <Server className="w-4 h-4" />;
    case 'Design':
      return <PenTool className="w-4 h-4" />;
    case 'Tools':
      return <Wrench className="w-4 h-4" />;
    case 'Soft Skills':
      return <Users className="w-4 h-4" />;
    default:
      return <Code className="w-4 h-4" />;
  }
};

/**
 * SkillCard Component
 * 
 * Displays a skill with its category, name, and a visual proficiency bar.
 * Adheres to the 'Physicality with Digital Precision' design philosophy.
 */
export function SkillCard({ skill }: SkillCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -4 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30
      }}
      className="group relative bg-card border border-border/50 rounded-xl p-6 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Background Accent Gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10 flex flex-col gap-4">
        <div className="flex justify-between items-start">
          <div className="flex flex-col gap-1">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground bg-muted/50 px-2 py-0.5 rounded-full w-fit">
              {getCategoryIcon(skill.category)}
              {skill.category}
            </span>
            <h3 className="text-lg font-semibold text-foreground mt-1">{skill.name}</h3>
          </div>
          <span className="text-sm font-mono font-medium text-primary">
            {skill.proficiency}%
          </span>
        </div>

        {/* Progress Bar Container */}
        <div className="space-y-2">
          <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: `${skill.proficiency}%` }}
              viewport={{ once: true }}
              transition={{ 
                duration: 1.2, 
                ease: [0.22, 1, 0.36, 1],
                delay: 0.2
              }}
              className="h-full bg-primary relative"
            >
              {/* Glow effect for the progress bar head */}
              <div className="absolute right-0 top-0 h-full w-4 bg-primary blur-sm opacity-50" />
            </motion.div>
          </div>
          
          {/* Subtle label for proficiency level if needed */}
          <div className="flex justify-between text-[10px] text-muted-foreground/60 font-medium">
            <span>BEGINNER</span>
            <span>EXPERT</span>
          </div>
        </div>
      </div>

      {/* Interactive Rim Light (Bottom Border) */}
      <div className="absolute bottom-0 left-0 h-[2px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
    </motion.div>
  );
}
